// src/components/modals/PaymentModal.jsx
import React, { useState, useEffect } from 'react';
import { X, CreditCard, DollarSign, Calculator, Check, Loader2, Award } from 'lucide-react';
import toast from 'react-hot-toast';
import ApiService from '../../services/api';
import OrderService from '../../services/orderService';
import CustomerService from '../../services/customerService';
import RedeemOtpService from '../../services/redeemOtpService';
import ReceiptService from '../../services/receiptService';
import { CustomerModel } from '../../utils/customerModel';
import { DEFAULT_CUSTOMERS } from '../../config/constants';
import { roundToCent } from '../../utils/currency';
import { HTMLReceiptGenerator } from '../../utils/htmlReceiptGenerator';
import BillTypeModal from './BillTypeModal';
import SuccessModal from './SuccessModal';

// Cash denomination constants for Sri Lanka (LKR)
const CASH_DENOMINATIONS = [100, 500, 1000, 5000];

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  cartItems = [], 
  orderCalculation = null, 
  orderType = 'dine_in',
  branchId,
  customerId = null,
  selectedTable = null,
  currentOrderId = null, // Current order ID for patching
  onOrderIdChange, // Order ID change callback
  deliveryProvider = null, // Delivery provider ('pickme' or 'uber')
  deliveryReference = '', // Delivery reference (last digit of order ID)
  onPaymentSuccess 
}) => {
  const [paymentMethod, setPaymentMethod] = useState('multiple'); // cash, card, redeem, multiple
  const [cashAmount, setCashAmount] = useState('');
  const [cardPayments, setCardPayments] = useState([{ amount: '', last4Digits: '' }]); // Array for multiple cards
  const [redeemPoints, setRedeemPoints] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeInput, setActiveInput] = useState('cash'); // Track which input is active for keypad
  const [activeCardIndex, setActiveCardIndex] = useState(0); // Track which card input is active
  const [actualOrderTotal, setActualOrderTotal] = useState(null); // Store actual order total from backend
  const [customerData, setCustomerData] = useState(null); // Store customer data with points
  const [fetchingCustomer, setFetchingCustomer] = useState(false); // Loading state for customer fetch
  
  // OTP Verification states
  const [otpLoading, setOtpLoading] = useState(false);
  const [redeemOtpId, setRedeemOtpId] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  
  // Bill type modal states
  const [showBillTypeModal, setShowBillTypeModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [completedOrder, setCompletedOrder] = useState(null);
  
  // Get order totals from calculation (always use fallback for display)
  const getOrderTotals = () => {
    // Use fallback calculation for display before order creation
    if (!orderCalculation) {
      return { subtotal: 0, discount: 0, serviceFee: 0, packageFee: 0, totalFees: 0, total: 0 };
    }

    const productPromotions = orderCalculation.productPromotions;
    const orderPromotions = orderCalculation.orderPromotions;
    
    const subtotal = productPromotions?.summary?.overallTotals?.originalPrice || 0;
    const productDiscount = productPromotions?.summary?.overallTotals?.totalDiscount || 0;
    const orderDiscount = orderPromotions?.orderDetails?.totalDiscount || 0;
    const totalDiscount = productDiscount + orderDiscount;
    const priceAfterDiscount = orderPromotions?.orderDetails?.finalOrderValue || (subtotal - totalDiscount);
    
    // Use precise calculation without rounding for service fee
    const serviceFee = priceAfterDiscount * 0.10;
    const total = roundToCent(priceAfterDiscount + serviceFee);
    
    return { 
      subtotal: roundToCent(Number(subtotal) || 0), 
      discount: roundToCent(Number(totalDiscount) || 0), 
      serviceFee: serviceFee, // Keep exact service fee without rounding
      packageFee: 0,
      totalFees: serviceFee, // Keep exact service fee without rounding
      total: total 
    };
  };

  const totals = getOrderTotals();

  // Get the display total - prioritize actual order total from backend over frontend calculation
  const getDisplayTotal = () => {
    const displayTotal = actualOrderTotal || totals.total;
    console.log('🔍 getDisplayTotal called:', {
      actualOrderTotal,
      frontendTotal: totals.total,
      displayTotal,
      orderType
    });
    return displayTotal;
  };

  // Reset form when modal opens (only on first open)
  useEffect(() => {
    if (isOpen) {
      // Don't reset if we're in the middle of OTP flow or if payment method is already selected
      if (redeemOtpId || paymentMethod === 'redeem' || paymentMethod === 'multiple') {
        console.log('🔒 Skipping reset - OTP flow or payment method already selected');
        return;
      }
      
      setPaymentMethod('cash');
      setCashAmount('');
      setCardPayments([{ amount: '', last4Digits: '' }]);
      setRedeemPoints('');
      setError('');
      setActiveInput('cash');
      setActualOrderTotal(null); // Reset actual order total when modal opens
      setCustomerData(null); // Reset customer data
      
      // Reset OTP verification states
      setOtpLoading(false);
      setRedeemOtpId(null);
      setOtpVerified(false);
      
      console.log('🔄 Payment modal opened, reset actualOrderTotal to null');
    } else {
      // When modal closes, always reset OTP states to prevent showing old data on next order
      console.log('🧹 Modal closed, cleaning up OTP states');
      setRedeemOtpId(null);
      setOtpVerified(false);
      setOtpLoading(false);
      setRedeemPoints('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen to prevent reset during OTP flow

  // Fetch order and customer data when needed
  useEffect(() => {
    if (isOpen) {
      // If we have a current order ID, fetch the order details to get the actual total
      if (currentOrderId) {
        fetchExistingOrderTotal(currentOrderId);
      }
      
      // Fetch customer data if customer ID is provided
      if (customerId && customerId !== DEFAULT_CUSTOMERS.WALKING_CUSTOMER) {
        fetchCustomerData(customerId);
      }
    }
  }, [isOpen, currentOrderId, customerId]);

  // Fetch existing order total when order already exists
  const fetchExistingOrderTotal = async (orderId) => {
    try {
      console.log('📋 Fetching existing order details for ID:', orderId);
      const response = await ApiService.get(`/orders/${orderId}`);
      
      console.log('📥 Full API Response:', response);
      
      // Handle both possible response structures
      let orderData = null;
      let totalAmount = null;
      
      if (response.success) {
        // Check if order is nested under response.data.order (your actual structure)
        if (response.data?.order?.orderPayment?.totalAmount) {
          orderData = response.data.order;
          totalAmount = response.data.order.orderPayment.totalAmount;
          console.log('✅ Found order data under response.data.order');
        }
        // Fallback: Check if order is directly under response.data
        else if (response.data?.orderPayment?.totalAmount) {
          orderData = response.data;
          totalAmount = response.data.orderPayment.totalAmount;
          console.log('✅ Found order data under response.data');
        }
        
        if (totalAmount) {
          setActualOrderTotal(totalAmount);
          console.log('💰 Existing order total fetched:', totalAmount);
          console.log('📊 Order payment details:', orderData.orderPayment);
          console.log('🎯 Order fees:', orderData.fees);
          console.log('🏷️ Order promotions:', orderData.orderPromotionEngineResult);
        } else {
          console.warn('⚠️ Could not find totalAmount in response structure');
          console.warn('📋 Available data keys:', Object.keys(response.data || {}));
          if (response.data?.order) {
            console.warn('📋 Order data keys:', Object.keys(response.data.order || {}));
          }
        }
      } else {
        console.warn('⚠️ API response not successful:', response);
      }
    } catch (error) {
      console.error('❌ Error fetching existing order:', error);
      // Don't show error to user, just log it and continue with frontend calculation
    }
  };

  // Fetch customer data for points redemption
  const fetchCustomerData = async (customerIdToFetch) => {
    try {
      setFetchingCustomer(true);
      console.log('🔍 Fetching customer data for points redemption:', customerIdToFetch);
      
      const response = await CustomerService.getCustomerById(customerIdToFetch);
      if (response?.success && response.data) {
        // Handle nested customer data structure
        const customer = response.data.customer || response.data;
        console.log('✅ Customer data fetched for payment:', customer);
        console.log('📊 Points structure:', {
          pointsBalance: customer.pointsBalance,
          lifetimePointsEarned: customer.lifetimePointsEarned,
          lifetimePointsRedeemed: customer.lifetimePointsRedeemed
        });
        
        // Store raw customer data with raw point values for calculations
        const formattedCustomer = {
          ...customer,
          // Keep raw numbers for calculations and display
          pointsBalance: customer.pointsBalance || 0,
          lifetimePointsEarned: customer.lifetimePointsEarned || 0,
          lifetimePointsRedeemed: customer.lifetimePointsRedeemed || 0
        };
        
        setCustomerData(formattedCustomer);
        console.log('💰 Customer points available:', formattedCustomer.pointsBalance);
      } else {
        console.warn('⚠️ Failed to fetch customer data:', response);
        setCustomerData(null);
      }
    } catch (error) {
      console.error('❌ Error fetching customer data:', error);
      setCustomerData(null);
    } finally {
      setFetchingCustomer(false);
    }
  };

  // Monitor actualOrderTotal changes
  useEffect(() => {
    console.log('💰 actualOrderTotal changed:', actualOrderTotal);
  }, [actualOrderTotal]);

  // Helper functions for card payments
  const addCardPayment = () => {
    setCardPayments(prev => [...prev, { amount: '', last4Digits: '' }]);
  };

  const removeCardPayment = (index) => {
    if (cardPayments.length > 1) {
      setCardPayments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateCardPayment = (index, field, value) => {
    setCardPayments(prev => prev.map((card, i) => 
      i === index ? { ...card, [field]: value } : card
    ));
  };

  // Auto-fill full amount when payment method is selected
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    const displayTotal = getDisplayTotal();
    
    if (method === 'cash') {
      setCashAmount(displayTotal.toString());
      setCardPayments([{ amount: '', last4Digits: '' }]);
      setRedeemPoints('');
      setActiveInput('cash');
    } else if (method === 'card') {
      setCashAmount('');
      setCardPayments([{ amount: displayTotal.toString(), last4Digits: '' }]);
      setRedeemPoints('');
      setActiveInput('card-0');
    } else if (method === 'redeem') {
      setCashAmount('');
      setCardPayments([{ amount: '', last4Digits: '' }]);
      
      // Use actual customer points balance or fallback to 0
      const availablePoints = customerData?.pointsBalance || 0;
      const maxRedeemable = Math.min(availablePoints, displayTotal);
      setRedeemPoints(maxRedeemable.toString());
      setActiveInput('redeem');
      
      // Show warning if customer has no points
      if (availablePoints === 0) {
        console.warn('⚠️ Customer has no points available for redemption');
      }
    } else {
      // Multiple - reset all
      setCashAmount('');
      setCardPayments([{ amount: '', last4Digits: '' }]);
      setRedeemPoints('');
      setActiveInput('cash');
    }
  };

  // Validate points redemption
  const validatePointsRedemption = (pointsToRedeem) => {
    if (!customerData) return { valid: false, message: "Customer data not loaded" };
    
    const availablePoints = customerData.pointsBalance || 0;
    const requestedPoints = parseFloat(pointsToRedeem) || 0;
    
    if (requestedPoints <= 0) return { valid: true, message: "" };
    if (requestedPoints > availablePoints) {
      return { 
        valid: false, 
        message: `Cannot redeem ${requestedPoints} points. Available: ${availablePoints}` 
      };
    }
    
    const displayTotal = getDisplayTotal();
    if (requestedPoints > displayTotal) {
      return { 
        valid: false, 
        message: `Cannot redeem more than order total (Rs.${displayTotal.toFixed(2)})` 
      };
    }
    
    return { valid: true, message: "" };
  };

  // Calculate balance and validate payment
  const getRemainingBalance = () => {
    const cash = parseFloat(cashAmount) || 0;
    const cardTotal = cardPayments.reduce((sum, card) => sum + (parseFloat(card.amount) || 0), 0);
    const redeem = parseFloat(redeemPoints) || 0;
    const totalPaid = cash + cardTotal + redeem;
    const displayTotal = getDisplayTotal();
    return Math.max(0, displayTotal - totalPaid);
  };

  const getChangeAmount = () => {
    const cash = parseFloat(cashAmount) || 0;
    const cardTotal = cardPayments.reduce((sum, card) => sum + (parseFloat(card.amount) || 0), 0);
    const redeem = parseFloat(redeemPoints) || 0;
    const totalPaid = cash + cardTotal + redeem;
    const displayTotal = getDisplayTotal();
    return Math.max(0, totalPaid - displayTotal);
  };

  // Cash denomination helpers
  const handleCashDenomination = (denomination) => {
    setCashAmount(denomination.toString());
    setActiveInput('cash');
  };

  // Clear all payment inputs
  const handleClearAll = () => {
    // Clear all payment amounts
    setCashAmount('');
    setCardPayments([{ amount: '', last4Digits: '' }]);
    setRedeemPoints('');
    
    // Reset to default state
    setActiveInput('cash');
    setActiveCardIndex(0);
    setError('');
    
    // Show feedback toast
    toast.success('Payment inputs cleared', {
      duration: 2000,
      position: 'top-center',
      style: {
        background: '#6B7280',
        color: '#fff',
        padding: '12px',
        borderRadius: '8px',
        fontSize: '14px'
      }
    });
  };

  // Card input helpers
  const handleCardFocus = (index, field = 'amount') => {
    setActiveCardIndex(index);
    if (field === 'last4Digits') {
      setActiveInput(`card-${index}-digits`);
    } else {
      setActiveInput(`card-${index}`);
    }
  };

  const handleCardInput = (index, field, value) => {
    if (field === 'last4Digits') {
      // Only allow 4 digits
      const digits = value.replace(/\D/g, '').slice(0, 4);
      updateCardPayment(index, field, digits);
    } else {
      updateCardPayment(index, field, value);
    }
  };

  // Keypad button handler
  const handleKeypadInput = (value) => {
    if (activeInput === 'cash') {
      if (value === 'CLR') {
        setCashAmount('');
      } else if (value === '.') {
        if (!cashAmount.includes('.')) {
          setCashAmount(prev => prev + '.');
        }
      } else if (value === 'ENT') {
        // Move to next input
        if (cardPayments.length > 0) {
          setActiveInput('card-0');
          setActiveCardIndex(0);
        } else if (paymentMethod === 'redeem' || paymentMethod === 'multiple') {
          setActiveInput('redeem');
        }
        return;
      } else {
        setCashAmount(prev => prev + value);
      }
    } else if (activeInput.startsWith('card-') && activeInput.includes('digits')) {
      // Handle card last 4 digits input
      const cardIndex = parseInt(activeInput.split('-')[1]);
      const currentDigits = cardPayments[cardIndex]?.last4Digits || '';
      
      if (value === 'CLR') {
        updateCardPayment(cardIndex, 'last4Digits', '');
      } else if (value === 'ENT') {
        // Switch to amount input for same card
        setActiveInput(`card-${cardIndex}`);
        return;
      } else if (value !== '.' && currentDigits.length < 4) {
        // Only add numbers, no decimal points for digits
        updateCardPayment(cardIndex, 'last4Digits', currentDigits + value);
      }
    } else if (activeInput.startsWith('card-')) {
      // Handle card amount input
      const cardIndex = parseInt(activeInput.split('-')[1]);
      const currentAmount = cardPayments[cardIndex]?.amount || '';
      
      if (value === 'CLR') {
        updateCardPayment(cardIndex, 'amount', '');
      } else if (value === '.') {
        if (!currentAmount.includes('.')) {
          updateCardPayment(cardIndex, 'amount', currentAmount + '.');
        }
      } else if (value === 'ENT') {
        // Move to next card or redeem
        if (cardIndex < cardPayments.length - 1) {
          setActiveInput(`card-${cardIndex + 1}`);
          setActiveCardIndex(cardIndex + 1);
        } else if (paymentMethod === 'redeem' || paymentMethod === 'multiple') {
          setActiveInput('redeem');
        }
        return;
      } else {
        updateCardPayment(cardIndex, 'amount', currentAmount + value);
      }
    } else if (activeInput === 'redeem') {
      if (value === 'CLR') {
        setRedeemPoints('');
      } else if (value === 'ENT') {
        return;
      } else if (value !== '.') { // Points should be whole numbers
        setRedeemPoints(prev => prev + value);
      }
    }
  };

  // Request OTP for points redemption
  const handleRequestOtp = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('📱 Requesting OTP for points redemption...');

      // Create or update order first to get order ID
      if (!currentOrderId) {
        // Need to create order first
        const orderData = {
          branchId: branchId,
          customerId: customerId || DEFAULT_CUSTOMERS.WALKING_CUSTOMER,
          orderType: getApiOrderType(orderType),
          orderItems: cartItems.map(item => ({
            productId: item.product?._id || item.productId || item.id,
            quantity: item.quantity || 1
          })).filter(item => item.productId)
        };

        // Add table for dine-in orders
        if (orderType === 'dine-in' && selectedTable?._id) {
          orderData.table = selectedTable._id;
        }

        // Add delivery provider for delivery orders
        if (orderType === 'delivery' && deliveryProvider) {
          orderData.deliveryProvider = deliveryProvider;
          if (deliveryReference) {
            orderData.deliveryReference = deliveryReference;
          }
        }

        console.log('📝 Creating order for OTP request:', orderData);
        const orderResponse = await OrderService.createOrder(orderData);

        if (!orderResponse.success || !orderResponse.data?.order) {
          throw new Error(orderResponse.message || 'Failed to create order');
        }

        const apiOrder = orderResponse.data.order;
        console.log('✅ Order created for OTP:', apiOrder._id);

        // Store order ID
        if (onOrderIdChange && apiOrder._id) {
          onOrderIdChange(apiOrder._id);
        }

        // Set actual order total
        if (apiOrder.orderPayment?.totalAmount) {
          setActualOrderTotal(apiOrder.orderPayment.totalAmount);
        }

        // Request OTP with the new order ID
        const redeem = parseFloat(redeemPoints) || 0;
        const otpResponse = await RedeemOtpService.requestRedeemOtp(apiOrder._id, redeem);

        if (otpResponse?.success && otpResponse.data?._id) {
          setRedeemOtpId(otpResponse.data._id);
          console.log('✅ OTP requested successfully:', otpResponse.data._id);
          console.log('📱 OTP Code (for testing):', otpResponse.data.otp);
          
          // Keep focus on redeem section
          setActiveInput('redeem');
          
          toast.success('OTP sent to customer! Please enter the code below.');
          // Focus first OTP input
          setTimeout(() => {
            window.otpInputRefs?.[0]?.focus();
          }, 300);
        } else {
          throw new Error('Failed to request OTP');
        }
      } else {
        // Use existing order ID
        const redeem = parseFloat(redeemPoints) || 0;
        const otpResponse = await RedeemOtpService.requestRedeemOtp(currentOrderId, redeem);

        if (otpResponse?.success && otpResponse.data?._id) {
          setRedeemOtpId(otpResponse.data._id);
          console.log('✅ OTP requested successfully:', otpResponse.data._id);
          console.log('📱 OTP Code (for testing):', otpResponse.data.otp);
          
          // Keep focus on redeem section
          setActiveInput('redeem');
          
          toast.success('OTP sent to customer! Please enter the code below.');
          // Focus first OTP input
          setTimeout(() => {
            window.otpInputRefs?.[0]?.focus();
          }, 300);
        } else {
          throw new Error('Failed to request OTP');
        }
      }
    } catch (error) {
      console.error('❌ Error requesting OTP:', error);
      setError(`Failed to request OTP: ${error.message}`);
      toast.error('Failed to request OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Convert order type to API format
  const getApiOrderType = (orderType) => {
    switch (orderType) {
      case 'dine-in':
        return 'dine_in';
      case 'takeaway':
        return 'take_away';
      case 'delivery':
        return 'delivery';
      default:
        return orderType.replace('-', '_'); // fallback for any other hyphenated types
    }
  };

  // Verify OTP and complete payment
  const handleVerifyOtp = async (otpCode) => {
    try {
      setOtpLoading(true);
      console.log('🔐 Verifying OTP:', otpCode);

      if (!redeemOtpId) {
        throw new Error('No OTP ID available for verification');
      }

      const verifyResponse = await RedeemOtpService.verifyRedeemOtp(redeemOtpId, otpCode);

      if (verifyResponse?.success) {
        console.log('✅ OTP verified successfully');
        toast.success('OTP verified! You can now complete the payment.');
        
        // Mark OTP as verified
        setOtpVerified(true);
        
        // Update customer data with new points balance if available
        if (verifyResponse.data?.customer) {
          const updatedCustomer = verifyResponse.data.customer;
          setCustomerData({
            ...customerData,
            pointsBalance: updatedCustomer.pointsBalance || 0,
            lifetimePointsRedeemed: updatedCustomer.lifetimePointsRedeemed || 0
          });
          console.log('✅ Customer points updated:', updatedCustomer.pointsBalance);
        }
        
        // Clear OTP inputs
        if (window.otpInputRefs) {
          window.otpInputRefs.forEach(input => {
            if (input) input.value = '';
          });
        }
      } else {
        throw new Error(verifyResponse?.message || 'OTP verification failed');
      }
    } catch (error) {
      console.error('❌ Error verifying OTP:', error);
      toast.error(error.message || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Handle E-Bill selection
  const handleEBill = async () => {
    if (!completedOrder || !completedOrder._id) {
      toast.error('Order ID not found');
      return;
    }

    setLoading(true);
    try {
      const response = await ReceiptService.sendEReceipt(completedOrder._id);
      console.log('✅ E-Receipt sent successfully:', response);
      
      // Close bill type modal
      setShowBillTypeModal(false);
      
      // Show success modal with message
      setSuccessMessage(response.message || 'E-Receipt SMS sent successfully');
      setShowSuccessModal(true);
      
      toast.success('E-Receipt sent successfully!');
    } catch (error) {
      console.error('❌ Error sending e-receipt:', error);
      toast.error(error.response?.data?.message || 'Failed to send e-receipt');
    } finally {
      setLoading(false);
    }
  };

  // Handle Paper Bill selection
  const handlePaperBill = () => {
    if (completedOrder) {
      // Close bill type modal
      setShowBillTypeModal(false);
      
      // Print receipt
      setTimeout(() => {
        printOrderReceipt(completedOrder);
      }, 300);
    }
  };

  // Print receipt after payment
  const printOrderReceipt = (order) => {
    try {
      console.log('🖨️ printOrderReceipt called with order:', order);
      console.log('🖨️ Order items:', order.orderItems);
      console.log('🖨️ Order payment:', order.orderPayment);
      
      // Get branch info from local storage if available
      const user = JSON.parse(localStorage.getItem('tezlaa-user') || '{}');
      const branchName = user.branch?.name || 'Tezlaa Cafe';
      
      // Prepare receipt options
      const receiptOptions = {
        companyName: branchName,
        companyAddress: user.branch?.address || 'Your Business Address',
        companyPhone: user.branch?.phoneNumber || 'Your Phone Number',
        companyEmail: user.branch?.email || 'contact@tezlaa.com'
      };
      
      console.log('🖨️ Receipt options:', receiptOptions);
      
      // Print receipt using HTMLReceiptGenerator
      HTMLReceiptGenerator.printReceipt(order, receiptOptions);
    } catch (error) {
      console.error('❌ Error printing receipt:', error);
      // Don't show error to user, as payment was successful
    }
  };

  // Handle payment submission (create/update order first, then process payment)
  const handleConfirmPayment = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔍 Payment Modal Debug Info:');
      console.log('  - Cart Items:', cartItems);
      console.log('  - Branch ID:', branchId);
      console.log('  - Customer ID:', customerId);
      console.log('  - Order Type:', orderType);
      console.log('  - Selected Table:', selectedTable);
      console.log('  - Current Order ID:', currentOrderId);

      // Validate that we have items to order
      if (!cartItems || cartItems.length === 0) {
        setError('No items in cart to process payment for');
        return;
      }

      // Validate required data
      if (!branchId) {
        setError('Branch information is missing. Please try again.');
        return;
      }

      // Validate payment amounts first
      const cash = parseFloat(cashAmount) || 0;
      const cardTotal = cardPayments.reduce((sum, card) => sum + (parseFloat(card.amount) || 0), 0);
      const redeem = parseFloat(redeemPoints) || 0;
      const totalPaid = cash + cardTotal + redeem;

      // Validate points redemption if points are being used
      if (redeem > 0) {
        const pointsValidation = validatePointsRedemption(redeemPoints);
        if (!pointsValidation.valid) {
          setError(pointsValidation.message);
          return;
        }
        console.log('✅ Points redemption validated:', redeem, 'points');
        
        // Check if OTP verification is required and not yet verified
        if (!otpVerified) {
          console.log('🔐 Points redemption requires OTP verification');
          setError('Please verify OTP before completing payment');
          return; // Stop here and require OTP verification
        }
        
        console.log('✅ OTP already verified, proceeding with payment');
      }

      let apiOrder;

      // Step 1: Create or Update Order (skip if order already exists and is current)
      if (currentOrderId) {
        console.log('💳 Order already exists with ID:', currentOrderId);
        // For existing orders, we should have the order details from fetchExistingOrderTotal
        apiOrder = {
          _id: currentOrderId,
          orderNumber: `Order-${currentOrderId.slice(-6)}`, // Fallback order number
          orderPayment: { totalAmount: actualOrderTotal || totals.total } // Use actual total if available
        };
        console.log('📋 Using existing order for payment processing');
      } else {
        console.log('💳 Step 1: Creating/Updating order...');
        
        // Prepare order data
        const orderData = {
          branchId: branchId,
          customerId: customerId || DEFAULT_CUSTOMERS.WALKING_CUSTOMER,
          orderType: getApiOrderType(orderType),
          orderItems: cartItems.map(item => ({
            productId: item.product?._id || item.productId || item.id,
            quantity: item.quantity || 1
          })).filter(item => item.productId)
        };

        // Add table for dine-in orders
        if (orderType === 'dine-in' && selectedTable?._id) {
          orderData.table = selectedTable._id;
        }

        // Add delivery provider for delivery orders
        if (orderType === 'delivery' && deliveryProvider) {
          orderData.deliveryProvider = deliveryProvider;
          if (deliveryReference) {
            orderData.deliveryReference = deliveryReference;
          }
        }

        let orderResponse;
        
        console.log('📝 Creating new order');
        console.log('📊 Order Data:', orderData);
        console.log('🏷️ Order Type:', orderType, '→ API Format:', getApiOrderType(orderType));
        orderResponse = await OrderService.createOrder(orderData);

        if (!orderResponse.success || !orderResponse.data?.order) {
          throw new Error(orderResponse.message || 'Failed to process order');
        }

        apiOrder = orderResponse.data.order;
        console.log('✅ Order processed successfully:', apiOrder);
        console.log('💰 Order Payment Details:', apiOrder.orderPayment);
        
        // Store the actual order total from backend response
        if (apiOrder.orderPayment?.totalAmount) {
          setActualOrderTotal(apiOrder.orderPayment.totalAmount);
          console.log('💰 Backend order total set to:', apiOrder.orderPayment.totalAmount);
          console.log('🔄 Display total will change from:', totals.total, 'to:', apiOrder.orderPayment.totalAmount);
        } else {
          console.warn('⚠️ No orderPayment.totalAmount found in response for order type:', orderType);
          console.warn('📋 Full orderPayment object:', apiOrder.orderPayment);
        }
        
        // Store order ID for future updates
        if (onOrderIdChange && apiOrder._id) {
          onOrderIdChange(apiOrder._id);
        }
      }

      // Get the actual total from API response (prioritize actualOrderTotal from state)
      const actualTotal = actualOrderTotal || apiOrder.orderPayment?.totalAmount || 0;
      console.log('💰 Final total for payment validation:', actualTotal, '(actualOrderTotal:', actualOrderTotal, ', apiOrder total:', apiOrder.orderPayment?.totalAmount, ')');
      
      // Validate order total before proceeding with payment
      if (actualTotal <= 0 && totalPaid > 0) {
        console.error('❌ Order total is 0 but payment amount is', totalPaid);
        console.error('🔧 This suggests the order was not properly calculated. Attempting to recalculate...');
        
        // If we have an existing order with invalid total, try to update it
        if (currentOrderId && cartItems.length > 0) {
          try {
            console.log('🔄 Attempting to recalculate order total...');
            
            // Prepare order update data
            const updateData = {
              branchId: branchId,
              customerId: customerId || DEFAULT_CUSTOMERS.WALKING_CUSTOMER,
              orderType: getApiOrderType(orderType),
              orderItems: cartItems.map(item => ({
                productId: item.product?._id || item.productId || item.id,
                quantity: item.quantity || 1
              })).filter(item => item.productId)
            };
            
            // Add table for dine-in orders
            if (orderType === 'dine-in' && selectedTable?._id) {
              updateData.table = selectedTable._id;
            }
            
            console.log('📊 Updating order with data:', updateData);
            const updateResponse = await OrderService.updateOrder(currentOrderId, updateData);
            
            if (updateResponse.success && updateResponse.data?.order?.orderPayment?.totalAmount) {
              const newTotal = updateResponse.data.order.orderPayment.totalAmount;
              setActualOrderTotal(newTotal);
              apiOrder.orderPayment = updateResponse.data.order.orderPayment;
              console.log('✅ Order recalculated successfully. New total:', newTotal);
            } else {
              throw new Error('Failed to recalculate order total');
            }
          } catch (recalcError) {
            console.error('❌ Failed to recalculate order:', recalcError);
            setError('Order total is invalid and cannot be recalculated. Please create a new order.');
            return;
          }
        } else {
          setError('Order total is invalid (0). Please recalculate the order before payment.');
          return;
        }
      }
      
      // Get the final total for payment processing (after potential recalculation)
      const finalTotal = actualOrderTotal || apiOrder.orderPayment?.totalAmount || 0;
      console.log('💰 Final total for payment processing:', finalTotal);
      
      // Step 2: Process Payment
      console.log('💳 Step 2: Processing payment for order:', apiOrder.orderNumber);

      let paymentResponse = null;

      // Process payment if amounts are provided
      if (totalPaid > 0) {
        // Validate payment amount against final total only when payment is being made
        if (totalPaid < finalTotal) {
          setError(`Payment amount (Rs.${totalPaid.toFixed(2)}) is insufficient. Total required: Rs.${finalTotal.toFixed(2)}`);
          return;
        }
        const paymentData = {
          orderId: apiOrder._id,
          paymentMethods: []
        };

        if (cash > 0) {
          // For cash payments, we need to determine the received amount vs the bill amount
          const billAmount = Math.min(cash, finalTotal - (cardTotal + redeem));
          const receivedAmount = cash; // The amount customer actually gave
          
          paymentData.paymentMethods.push({
            method: 'cash',
            amount: billAmount, // Amount applied to the bill
            cashDetails: {
              receivedAmount: receivedAmount // Amount customer actually gave
            }
          });
        }

        // Add card payments (can be multiple)
        cardPayments.forEach((card) => {
          const cardAmount = parseFloat(card.amount) || 0;
          if (cardAmount > 0) {
            paymentData.paymentMethods.push({
              method: 'card',
              amount: cardAmount,
              cardDetails: {
                lastFourDigits: card.last4Digits || '0000' // Use provided digits or default
              }
            });
          }
        });

        if (redeem > 0) {
          paymentData.paymentMethods.push({
            method: 'redeem',
            amount: redeem,
            redeemDetails: {
              pointsUsed: redeem // Assuming 1:1 points to currency ratio
            }
          });
        }

        console.log('💳 Processing payment with data:', paymentData);

        try {
          paymentResponse = await ApiService.post('/order-payments', paymentData);
          console.log('✅ Payment processed successfully:', paymentResponse);

          toast.success('Payment completed successfully!');

          // Update order with payment data if available
          if (paymentResponse?.data?.order) {
            apiOrder.orderPayment = paymentResponse.data.order.orderPayment;
            apiOrder.paymentStatus = 'paid';
          }

          // Call the success callback with the updated order
          if (onPaymentSuccess) {
            onPaymentSuccess({
              order: apiOrder,
              payment: paymentResponse?.data
            });
          }

          // Store completed order and show bill type modal instead of immediately printing
          setCompletedOrder(apiOrder);
          
          // Reset OTP states before closing payment modal
          setRedeemOtpId(null);
          setOtpVerified(false);
          setOtpLoading(false);
          
          // Show bill type modal first, then close payment modal with delay
          setShowBillTypeModal(true);
          setTimeout(() => {
            onClose();
          }, 100);

        } catch (paymentError) {
          console.error('❌ Payment processing failed:', paymentError);
          throw new Error(`Payment failed: ${paymentError.message}`);
        }
      } else {
        console.log('ℹ️ Order created as unpaid - no payment amounts entered');
        
        // Show success message for unpaid order
        toast.success(`Order ${apiOrder.orderNumber} created successfully! (Unpaid)`);
        
        if (onPaymentSuccess) {
          onPaymentSuccess({
            order: apiOrder,
            payment: null
          });
        }
        
        // Reset OTP states before closing
        setRedeemOtpId(null);
        setOtpVerified(false);
        setOtpLoading(false);
        
        onClose();
      }

    } catch (error) {
      console.error('❌ Error processing order/payment:', error);
      setError(`Failed to process: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !showBillTypeModal && !showSuccessModal) return null;

  return (
    <>
      {/* Payment Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-orange-500 text-white rounded-t-lg flex-shrink-0">
          <h2 className="text-2xl font-bold">Complete Payment</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Order Summary */}
          <div className="w-1/2 p-6 border-r overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-4">Bill Amount</h3>
              <div className="text-right">
                <div className="text-4xl font-bold text-gray-900">
                  Rs.{getDisplayTotal().toFixed(2)}
                </div>
              </div>
            </div>

            {/* Customer Information */}
            {customerId && customerId !== DEFAULT_CUSTOMERS.WALKING_CUSTOMER && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center">
                  <Award className="h-4 w-4 mr-2" />
                  Customer Loyalty
                </h4>
                {fetchingCustomer ? (
                  <div className="flex items-center text-sm text-blue-600">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Loading customer data...
                  </div>
                ) : customerData ? (
                  <div className="space-y-1 text-sm">
                    <div className="font-medium text-blue-900">{customerData.name}</div>
                    <div className="text-blue-700">{customerData.mobileNumber}</div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-200">
                      <span className="text-blue-600">Available Points:</span>
                      <span className="font-bold text-blue-800">{customerData.pointsBalance || 0}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs text-blue-600">
                      <span>Lifetime Earned:</span>
                      <span>{customerData.lifetimePointsEarned || 0}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-blue-600">Customer data unavailable</div>
                )}
              </div>
            )}

            {/* Payment Method Selection */}
            <div className="mb-6">
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => handlePaymentMethodChange('cash')}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    paymentMethod === 'cash' 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cash
                </button>
                <button
                  onClick={() => handlePaymentMethodChange('card')}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    paymentMethod === 'card' 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Card
                </button>
                <button
                  onClick={() => handlePaymentMethodChange('redeem')}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    paymentMethod === 'redeem' 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Redeem
                </button>
                <button
                  onClick={() => handlePaymentMethodChange('multiple')}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    paymentMethod === 'multiple' 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Multiple
                </button>
              </div>
            </div>

            {/* Payment Input Fields */}
            {(paymentMethod === 'cash' || paymentMethod === 'multiple') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cash Amount
                </label>
                <input
                  type="text"
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg text-xl text-right font-mono bg-white"
                  placeholder="0.00"
                  readOnly={paymentMethod !== 'cash' && paymentMethod !== 'multiple'}
                />
              </div>
            )}

            {(paymentMethod === 'card' || paymentMethod === 'multiple') && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Card Payments
                    <span className="text-xs text-gray-500 ml-2">Last 4 digits</span>
                  </label>
                  {paymentMethod === 'multiple' && cardPayments.length < 3 && (
                    <button
                      onClick={addCardPayment}
                      className="text-orange-500 hover:text-orange-600 font-medium text-sm flex items-center"
                    >
                      <span className="mr-1">+</span> Add Card
                    </button>
                  )}
                </div>
                
                <div className="space-y-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {cardPayments.map((card, index) => (
                    <div key={index} className="flex gap-2 bg-white p-2 rounded-lg border">
                      <input
                        type="text"
                        value={card.amount}
                        onChange={(e) => handleCardInput(index, 'amount', e.target.value)}
                        onFocus={() => handleCardFocus(index, 'amount')}
                        className="flex-1 p-3 border border-gray-300 rounded-lg text-lg text-right font-mono bg-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                        placeholder="0.00"
                        readOnly={paymentMethod !== 'card' && paymentMethod !== 'multiple'}
                      />
                      <input
                        type="text"
                        value={card.last4Digits}
                        onChange={(e) => handleCardInput(index, 'last4Digits', e.target.value)}
                        onFocus={() => handleCardFocus(index, 'last4Digits')}
                        className="w-20 p-3 border border-gray-300 rounded-lg text-center font-mono bg-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                        placeholder="****"
                        maxLength="4"
                      />
                      {paymentMethod === 'multiple' && cardPayments.length > 1 && (
                        <button
                          onClick={() => removeCardPayment(index)}
                          className="px-3 py-2 text-red-500 hover:text-red-600 font-medium rounded-lg hover:bg-red-50"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(paymentMethod === 'redeem' || paymentMethod === 'multiple') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Redeem Points
                  {fetchingCustomer ? (
                    <span className="text-xs text-gray-500 ml-2">Loading...</span>
                  ) : customerData ? (
                    <span className="text-xs text-gray-500 ml-2">
                      Available: {customerData.pointsBalance || 0} points
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500 ml-2">Customer points unavailable</span>
                  )}
                </label>
                <input
                  type="text"
                  value={redeemPoints}
                  onChange={(e) => setRedeemPoints(e.target.value)}
                  onFocus={() => setActiveInput('redeem')}
                  className="w-full p-4 border border-gray-300 rounded-lg text-xl text-right font-mono bg-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                  placeholder="0"
                  disabled={!customerData || (customerData.pointsBalance || 0) === 0}
                />
                <div className="flex justify-between items-center mt-2">
                  <button 
                    onClick={() => {
                      const availablePoints = customerData?.pointsBalance || 0;
                      const maxPoints = Math.min(availablePoints, getDisplayTotal());
                      setRedeemPoints(maxPoints.toString());
                      setActiveInput('redeem');
                    }}
                    disabled={!customerData || (customerData.pointsBalance || 0) === 0}
                    className="text-orange-500 text-sm font-medium hover:text-orange-600 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Max ({customerData ? Math.min(customerData.pointsBalance || 0, getDisplayTotal()) : 0})
                  </button>
                  {customerData && (
                    <div className="flex items-center text-xs text-gray-500">
                      <Award className="h-3 w-3 mr-1" />
                      <span>1 point = Rs.1.00</span>
                    </div>
                  )}
                </div>

                {/* OTP Section - Show when points are entered and not yet verified */}
                {parseFloat(redeemPoints) > 0 && !otpVerified && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-2 border-orange-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="h-8 w-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                          <Award className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">OTP Verification Required</h4>
                          <p className="text-xs text-gray-600">
                            {paymentMethod === 'multiple' 
                              ? `Verify to redeem ${redeemPoints} points + pay remaining with cash/card`
                              : 'Enter 6-digit code sent to customer'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {!redeemOtpId ? (
                      /* Request OTP Button */
                      <button
                        onClick={handleRequestOtp}
                        disabled={loading}
                        className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span>Requesting OTP...</span>
                          </>
                        ) : (
                          <>
                            <Award className="h-5 w-5" />
                            <span>Request OTP to Redeem {redeemPoints} Points</span>
                          </>
                        )}
                      </button>
                    ) : (
                      /* OTP Input Section */
                      <div className="space-y-3">
                        <div className="flex justify-center gap-2">
                          {[0, 1, 2, 3, 4, 5].map((index) => (
                            <input
                              key={index}
                              ref={(el) => {
                                if (!window.otpInputRefs) window.otpInputRefs = [];
                                window.otpInputRefs[index] = el;
                              }}
                              type="text"
                              maxLength="1"
                              className="w-10 h-12 text-center text-xl font-bold border-2 border-orange-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all"
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                if (value && index < 5) {
                                  window.otpInputRefs[index + 1]?.focus();
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Backspace' && !e.target.value && index > 0) {
                                  window.otpInputRefs[index - 1]?.focus();
                                }
                              }}
                              disabled={otpLoading}
                            />
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const otpCode = Array.from({ length: 6 }, (_, i) => 
                                window.otpInputRefs?.[i]?.value || ''
                              ).join('');
                              if (otpCode.length === 6) {
                                handleVerifyOtp(otpCode);
                              } else {
                                toast.error('Please enter complete 6-digit OTP');
                              }
                            }}
                            disabled={otpLoading}
                            className="flex-1 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                          >
                            {otpLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Verifying...</span>
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4" />
                                <span>Verify OTP</span>
                              </>
                            )}
                          </button>

                          <button
                            onClick={handleRequestOtp}
                            disabled={loading || otpLoading}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all disabled:opacity-50"
                          >
                            Resend
                          </button>
                        </div>

                        <p className="text-xs text-center text-gray-600">
                          Customer: <span className="font-semibold">{customerData?.name || 'Unknown'}</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* OTP Verified Indicator */}
                {parseFloat(redeemPoints) > 0 && otpVerified && (
                  <div className="mt-3 p-3 bg-green-50 border-2 border-green-300 rounded-lg">
                    <div className="flex flex-col items-center justify-center space-y-1 text-green-700">
                      <div className="flex items-center space-x-2">
                        <Check className="h-5 w-5" />
                        <span className="font-semibold">OTP Verified - {redeemPoints} points ready to redeem</span>
                      </div>
                      {paymentMethod === 'multiple' && (() => {
                        const remainingAfterPoints = getDisplayTotal() - parseFloat(redeemPoints);
                        return remainingAfterPoints > 0 ? (
                          <p className="text-xs text-green-600">
                            Remaining balance: Rs.{remainingAfterPoints.toFixed(2)} (Pay with cash/card)
                          </p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Summary */}
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between text-lg">
                <span>Total Bill Amount</span>
                <span>Rs.{getDisplayTotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span>Payable Amount</span>
                <span>Rs.{getDisplayTotal().toFixed(2)}</span>
              </div>
              
              {getRemainingBalance() > 0 ? (
                <div className="flex justify-between text-lg font-semibold text-red-600">
                  <span>Balance Due</span>
                  <span>Rs.{getRemainingBalance().toFixed(2)}</span>
                </div>
              ) : (
                <div className="flex justify-between text-lg font-semibold text-green-600">
                  <span>Change Due</span>
                  <span>Rs.{getChangeAmount().toFixed(2)}</span>
                </div>
              )}
              
              {/* Payment Breakdown */}
              <div className="border-t pt-3 space-y-2">
                {parseFloat(cashAmount) > 0 && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Cash:</span>
                    <span>Rs.{parseFloat(cashAmount).toFixed(2)}</span>
                  </div>
                )}
                {cardPayments.map((card, index) => {
                  const amount = parseFloat(card.amount) || 0;
                  return amount > 0 ? (
                    <div key={index} className="flex justify-between text-sm text-gray-600">
                      <span>Card {index + 1} ({card.last4Digits || '****'}):</span>
                      <span>Rs.{amount.toFixed(2)}</span>
                    </div>
                  ) : null;
                })}
                {parseFloat(redeemPoints) > 0 && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Redeem Points:</span>
                    <span>Rs.{parseFloat(redeemPoints).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Right Panel - Keypad */}
          <div className="w-1/2 p-6 overflow-y-auto">
            {/* Active Input Indicator */}
            <div className="mb-4 p-3 bg-orange-100 rounded-lg">
              <span className="text-sm font-medium text-orange-800">
                Active Input: {
                  activeInput === 'cash' ? 'Cash Amount' :
                  activeInput.startsWith('card-') ? `Card ${activeCardIndex + 1} ${activeInput.includes('digits') ? '(Last 4 Digits)' : '(Amount)'}` :
                  activeInput === 'redeem' ? 'Redeem Points' : activeInput
                }
              </span>
            </div>

            {/* Cash Denomination Buttons */}
            {(paymentMethod === 'cash' || paymentMethod === 'multiple') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Quick Cash Amounts</label>
                <div className="grid grid-cols-4 gap-2">
                  {CASH_DENOMINATIONS.map((denomination) => (
                    <button
                      key={denomination}
                      onClick={() => handleCashDenomination(denomination)}
                      className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors"
                    >
                      {denomination}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {/* Row 1 */}
              <button
                onClick={() => handleKeypadInput('7')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                7
              </button>
              <button
                onClick={() => handleKeypadInput('8')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                8
              </button>
              <button
                onClick={() => handleKeypadInput('9')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                9
              </button>

              {/* Row 2 */}
              <button
                onClick={() => handleKeypadInput('4')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                4
              </button>
              <button
                onClick={() => handleKeypadInput('5')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                5
              </button>
              <button
                onClick={() => handleKeypadInput('6')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                6
              </button>

              {/* Row 3 */}
              <button
                onClick={() => handleKeypadInput('1')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                1
              </button>
              <button
                onClick={() => handleKeypadInput('2')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                2
              </button>
              <button
                onClick={() => handleKeypadInput('3')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                3
              </button>

              {/* Row 4 */}
              <button
                onClick={() => handleKeypadInput('0')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                0
              </button>
              <button
                onClick={() => handleKeypadInput('.')}
                className="bg-orange-500 text-white py-4 rounded-lg text-2xl font-bold hover:bg-orange-600 transition-colors"
              >
                .
              </button>
              <button
                onClick={() => handleKeypadInput('CLR')}
                className="bg-red-500 text-white py-4 rounded-lg text-xl font-bold hover:bg-red-600 transition-colors"
              >
                CLR
              </button>
            </div>

            {/* Bottom Row - ENT and Clear */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                onClick={() => handleKeypadInput('ENT')}
                className="bg-gray-700 text-white py-4 rounded-lg text-xl font-bold hover:bg-gray-600 transition-colors"
              >
                ENT
              </button>
              <button
                onClick={handleClearAll}
                disabled={loading}
                className="bg-red-500 text-white py-4 rounded-lg text-xl font-bold hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                Clear
              </button>
            </div>

            {/* Pay Button - Handles order creation/update and payment in one step */}
            <button
              onClick={handleConfirmPayment}
              disabled={loading || !cartItems || cartItems.length === 0 || (() => {
                const cash = parseFloat(cashAmount) || 0;
                const cardTotal = cardPayments.reduce((sum, card) => sum + (parseFloat(card.amount) || 0), 0);
                const redeem = parseFloat(redeemPoints) || 0;
                const totalPaid = cash + cardTotal + redeem;
                return totalPaid <= 0; // Disable if no payment method has an amount
              })()}
              className="w-full bg-green-500 text-white py-4 rounded-lg text-xl font-bold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <Check className="h-5 w-5 mr-2" />
              )}
              {(() => {
                const cash = parseFloat(cashAmount) || 0;
                const cardTotal = cardPayments.reduce((sum, card) => sum + (parseFloat(card.amount) || 0), 0);
                const redeem = parseFloat(redeemPoints) || 0;
                const totalPaid = cash + cardTotal + redeem;
                return totalPaid <= 0 ? 'Enter Payment Amount' : 'Pay';
              })()}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t bg-gray-50 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {getRemainingBalance() > 0 ? (
                <span className="text-red-600 font-medium">Balance Due: Rs.{getRemainingBalance().toFixed(2)}</span>
              ) : (
                <span className="text-green-600 font-medium">Ready to process payment</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
        </div>
      )}
      
      {/* Bill Type Modal - Outside payment modal so it shows after payment modal closes */}
      <BillTypeModal
        isOpen={showBillTypeModal}
        onClose={() => setShowBillTypeModal(false)}
        onEBill={handleEBill}
        onPaperBill={handlePaperBill}
        isLoading={loading}
      />
      
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
        autoCloseDelay={2000}
      />
    </>
  );
};

export default PaymentModal;
